/**
 * DrawingImport — upload a construction drawing (PDF/image), AI extracts spec
 * + BoQ, user can apply extracted values to the calculator form.
 */
import React, { useState, useMemo } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, Loader2, Wand2, Check, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import InfoTip from './InfoTip';

import { makeFmt } from './currency';

export default function DrawingImport({ onApply, calculation = null }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    setFile(f);
    setData(null);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const analyze = async () => {
    if (!file) return;
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await ConstructionAPI.analyzeDrawing(fd);
      if (res?.ok === false) {
        toast.error(res.error || 'Analysis failed');
        setData(res);
        return;
      }
      setData(res);
      toast.success(`Drawing analysed (confidence: ${res.confidence || 'medium'})`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!data) return;
    onApply?.({
      gross_floor_area_m2: data.estimated_gfa_m2 || undefined,
      sub_sector: data.sub_sector_key || undefined,
      quantities: data.suggested_quantities || [],
      key_observations: data.key_observations || [],
      sourceConfidence: data.confidence,
    });
    toast.success('Applied to calculator');
  };

  const reset = () => { setFile(null); setPreview(null); setData(null); };

  return (
    <Card data-testid="drawing-import">
      <CardHeader className="pb-3">
        <CardTitle className="text-base inline-flex items-center gap-1.5">
          <Wand2 className="h-4 w-4 text-fuchsia-600" />
          Import drawing for auto-BoQ
          <InfoTip>Upload an architectural / structural drawing (image or PDF). Claude Sonnet 4.5 will extract gross floor area, levels, structure system and a rough BoQ. Best-effort — review before computing.</InfoTip>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${file ? 'border-fuchsia-300 bg-fuchsia-50/30' : 'border-slate-300 hover:bg-slate-50'}`} data-testid="drawing-upload-label">
          <Upload className="h-7 w-7 text-slate-400 mb-2" />
          <span className="text-sm text-slate-700 font-medium">{file ? `Selected: ${file.name}` : 'Click to upload a drawing'}</span>
          <span className="text-xs text-slate-500 mt-1">PDF or image (PNG / JPG / WEBP), up to 10 MB</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} data-testid="drawing-upload-input" />
        </label>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={analyze} disabled={!file || loading} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="drawing-analyze-btn">
            {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Analysing… (15-45s)</> : <><Wand2 className="h-3.5 w-3.5 mr-1.5" />Analyse with AI</>}
          </Button>
          {data?.ok && <Button variant="outline" onClick={apply} data-testid="drawing-apply-btn"><Check className="h-3.5 w-3.5 mr-1.5" />Apply to calculator</Button>}
          {file && <Button variant="ghost" onClick={reset} data-testid="drawing-remove-btn"><X className="h-3.5 w-3.5 mr-1.5" />Remove</Button>}
        </div>

        {file && preview && (
          <img src={preview} alt="preview" className="w-full max-h-56 object-contain border border-slate-200 rounded-md bg-white" />
        )}

        {data?.ok && (
          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-md p-3 text-sm space-y-2" data-testid="drawing-result">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-fuchsia-900">Extraction</span>
              <Badge className={`${data.confidence === 'high' ? 'bg-emerald-600' : data.confidence === 'medium' ? 'bg-amber-500' : 'bg-slate-500'} text-white text-xs`}>
                {data.confidence || 'medium'} confidence
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Stat label="Drawing type" value={data.drawing_type} />
              <Stat label="Project guess" value={data.project_type_guess} />
              <Stat label="GFA (m²)" value={Number(data.estimated_gfa_m2 || 0).toLocaleString()} />
              <Stat label="Levels" value={data.estimated_levels} />
              <Stat label="Footprint (m²)" value={Number(data.building_footprint_m2 || 0).toLocaleString()} />
              <Stat label="Structure" value={data.structure_system_guess} />
              <Stat label="Envelope" value={data.envelope_guess} />
              <Stat label="Sub-sector" value={data.sub_sector_key} />
            </div>
            {data.key_observations?.length > 0 && (
              <div className="text-xs text-fuchsia-900">
                <strong>Observations:</strong>
                <ul className="mt-0.5 ml-4 list-disc space-y-0.5">
                  {data.key_observations.slice(0, 5).map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {data.boq_lines?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-fuchsia-900 mb-1">Suggested BoQ ({data.boq_lines.length} lines)</div>
                <div className="overflow-x-auto bg-white rounded border border-fuchsia-200">
                  <table className="w-full text-xs">
                    <thead className="bg-fuchsia-100/50">
                      <tr>
                        <th className="text-left p-1.5">Phase</th>
                        <th className="text-left p-1.5">Description</th>
                        <th className="text-right p-1.5">Qty</th>
                        <th className="text-left p-1.5">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.boq_lines.slice(0, 12).map((ln, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1.5 capitalize">{(ln.phase || '').replace('_', ' ')}</td>
                          <td className="p-1.5">{ln.description}</td>
                          <td className="p-1.5 text-right">{Number(ln.quantity || 0).toLocaleString()}</td>
                          <td className="p-1.5">{ln.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {data.limitations && (
              <div className="text-[11px] text-slate-600 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0 mt-0.5" />
                <span>{data.limitations}</span>
              </div>
            )}
          </div>
        )}
        {data?.ok === false && (
          <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900" data-testid="drawing-error">
            <strong>Could not analyse:</strong> {data.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-fuchsia-100 rounded p-2">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="font-medium text-slate-800 truncate">{value || '—'}</div>
    </div>
  );
}
